/**
 *
 * InvoiceDetail
 *
 */

import React, { useState, useMemo, useEffect } from 'react';
import { useSafeState } from '@domonda/react-plumb';
import { FormattedMessage } from 'lib/intl';
import { ResolveOnTrigger } from 'lib/ResolveOnTrigger';
import store from 'store';

// api
import {
  buildDownloadInvoiceURL,
  Invoice,
  InvoicePage,
  SaveInvoiceParams,
  DeleteInvoiceParams,
} from 'lib/api/invoice';
import { flattenDetectInvoiceResponse } from 'lib/api/flatInvoiceDetails';
import { ocrBoundingBoxToPosition, OCRWord } from 'lib/api/ocrResult';

// ui
import { Grid, Box, Flex, Dialog, Err, ErrInline, Text, Button } from '@domonda/ui';
import { OCRTextPageDetection } from 'modules/OCRTextPage';
import { CircularProgress } from '@material-ui/core';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// form
import { Form, FormSubmitErrorState, FormSubmittingState } from '@domonda/react-form';

//context
import ErrorCountProvider from 'lib/api/invoiceErrorCountContext';

// parts
import { InvoiceDetailActionBar } from '../InvoiceDetailActionBar';
import { InvoiceDetailDetections } from '../InvoiceDetailDetections';
import { InvoiceDetailPage } from '../InvoiceDetailPage';
import { LassoBox } from '../../Lasso/LassoBox';

// selectors
import { deriveFormValues } from './selectors';

// decorate
import { decorate, Decorate } from './decorate';
import { getConfigFromStore } from 'lib/api/customerConfig';
import { SupplierResult } from 'lib/api/invoice';
import { useIntl } from 'react-intl';
import { getInvoiceDetailTypesOrder } from 'lib/api/invoiceDetails';

export interface InvoiceDetailProps {
  containerId: UUID;
  prevContainerUrl: string;
  nextContainerUrl: string;
  closeContainerUrl: string;
  badClientId?: string;
  badSupplierId?: string;
  SupplierResult: SupplierResult;
  invoice: Invoice;
  onDeleteInvoice: (params: DeleteInvoiceParams) => Promise<void>;
  onSaveInvoice: (params: SaveInvoiceParams) => Promise<void>;
}

interface PageWithOCRTextPageDetection extends InvoicePage {
  detections: OCRTextPageDetection[];
}

const InvoiceDetail: React.FC<InvoiceDetailProps & Decorate> = (props) => {
  const {
    badClientId,
    badSupplierId,
    containerId,
    prevContainerUrl,
    nextContainerUrl,
    closeContainerUrl,
    invoice,
    onDeleteInvoice,
    onSaveInvoice,
  } = props;
  const invoiceDownloadUrl = useMemo(() => buildDownloadInvoiceURL(invoice.InvoiceId), [invoice]);

  const pagesWithDetections = useMemo<PageWithOCRTextPageDetection[]>(() => {
    const ocrResult = invoice.DetectInvoiceResponse.OcrResult;
    if (!ocrResult) {
      return invoice.Pages.map((page) => ({ ...page, detections: [] }));
    }

    const { invoiceDetailsMap, invoiceDetailsGroupsMap } = flattenDetectInvoiceResponse(
      invoice.DetectInvoiceResponse,
    );

    const detections = [
      ...invoiceDetailsMap,
      ...invoiceDetailsGroupsMap.flatMap(({ InvoiceDetailsMap }) => InvoiceDetailsMap),
    ];
    // ordered page heights where the height is the previous page height + current one
    const pageAppendedHeights = ocrResult.Pages.concat()
      .sort(({ Index: leftIndex }, { Index: rightIndex }) => leftIndex - rightIndex)
      .reduce<number[]>((acc, { Height }, index) => {
        if (index === 0) {
          return [Height];
        }
        return [...acc, Height + acc[index - 1]];
      }, []);

    // map the detections to pages where they fit and deduce the prev page height
    return invoice.Pages.map((page, index) => {
      const currAppendedHeight = pageAppendedHeights[index];
      const prevAppendedHeight = index === 0 ? 0 : pageAppendedHeights[index - 1];
      return {
        ...page,
        detections: detections
          .filter(({ Y }) => Y >= prevAppendedHeight && currAppendedHeight >= Y)
          .map((detection) => ({ ...detection, Y: detection.Y - prevAppendedHeight })),
      };
    });
  }, [invoice]);

  const [fullscreen, setFullscreen] = useState(false);
  const [outlined, setOutlined] = useState(false);
  const [magnifier, setMagnifier] = useState(false);
  const [highlightedDetection, setHighlightedDetection] = useState<OCRTextPageDetection | null>(
    null,
  );

  const { formatMessage } = useIntl();

  const [deleteDialog, setDeleteDialog] = useSafeState(false);
  const [lassoActive, setLassoActive] = useState(true);
  const [scale, setScale] = useState(0);
  const eagerLoadCount = 5;

  //if there is no saved zoom value the whole document should fit on the screen
  //this is used to calculate the desired width of the document so it would fit on the screen
  //84 and 105 are the heights of the two headers above the document (the action bar and the nav bar)
  //0.73 is the width/height ratio of the document
  const calc = (window.innerHeight - 84 - 105) * 0.73;
  const [zoomWidth, setZoomWidth] = useState(
    localStorage.getItem('zoom') ? localStorage.getItem('zoom') : calc.toString(),
  );
  const onZoomInClick = (_e: any) => {
    let zoomNum = Number(zoomWidth);
    zoomNum += 300;
    localStorage.setItem('zoom', zoomNum.toString());
    setZoomWidth(zoomNum.toString());
  };
  const onZoomOutClick = (_e: any) => {
    let zoomNum = Number(zoomWidth);
    zoomNum -= 300;
    localStorage.setItem('zoom', zoomNum.toString());
    setZoomWidth(zoomNum.toString());
  };
  const [cropperCoordinates, setCrooperCoordinates] = useState({
    x1: 0,
    y1: 0,
    x2: 0,
    y2: 0,
  });
  const [croppedScrollCoords, setCroppedScrollCoords] = useState({ x1: 0, y1: 0, x2: 0, y2: 0 });
  const [displayLassoArea, setDisplayLassoArea] = useState(false);
  const [totalOffest, setTotalOffset] = useState<number>(0);
  const returnHeight = (id: string | undefined, height: number) => {
    if (id === `invoice-page-${pagesWithDetections[pagesWithDetections.length - 1].PageNo}`)
      setTotalOffset(height);
  };

  const onScaleChange = (s: number) => {
    setScale(s);
  };

  const invoiceOnMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const x1 = e.clientX;
    const y1 = e.clientY;

    setCrooperCoordinates({ ...cropperCoordinates, ...{ x1, y1 } });
    const container = document.getElementById('invoicePreviewer');
    if (container) {
      // const rect = container.getBoundingClientRect();
      const x21 = e.clientX + container.scrollLeft;
      const y21 = e.clientY + container.scrollTop;
      setCroppedScrollCoords({ ...croppedScrollCoords, ...{ x1: x21, y1: y21 } });
    }
    setDisplayLassoArea(true);
  };

  const invoiceOnMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const container = document.getElementById('invoicePreviewer');
    if (container) {
      // const rect = container.getBoundingClientRect();
      const x2 = e.clientX;
      const y2 = e.clientY;
      setCrooperCoordinates({ ...cropperCoordinates, ...{ x2, y2 } });
      const x22 = e.clientX + container.scrollLeft;
      const y22 = e.clientY + container.scrollTop;
      setCroppedScrollCoords({ ...croppedScrollCoords, ...{ x2: x22, y2: y22 } });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<Element>) => {
    if (e.shiftKey && e.keyCode === 107)
      setZoomWidth((old) => {
        let oz = Number(old);
        oz += 200;
        localStorage.setItem('zoom', oz.toString());
        return oz.toString();
      });
    else if (e.shiftKey && e.keyCode === 109)
      setZoomWidth((old) => {
        let oz = Number(old);
        oz -= 200;
        localStorage.setItem('zoom', oz.toString());
        return oz.toString();
      });
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown as any);
    return () => {
      document.removeEventListener('keydown', handleKeyDown as any);
    };
  }, []);

  const invoiceOnMouseUp = (_e: React.MouseEvent<HTMLDivElement>) => {

    setDisplayLassoArea(false);
    const foundElements: OCRWord[] = [];
    const invoicePreviewerEl = document.getElementById('invoicePreviewer');
    const lassoArea = document.getElementById('cropperArea');
    if (invoicePreviewerEl && lassoArea) {
      const viewportOffset = invoicePreviewerEl.getBoundingClientRect();

      const croppedArealeft =
        Math.min(croppedScrollCoords.x1, croppedScrollCoords.x2) - viewportOffset.left;
      const croppedAreaTop =
        Math.min(croppedScrollCoords.y1, croppedScrollCoords.y2) - viewportOffset.top;
      const croppedAreaWidth =
        Math.max(croppedScrollCoords.x1, croppedScrollCoords.x2) -
        Math.min(croppedScrollCoords.x1, croppedScrollCoords.x2);
      const croppedAreaHeight =
        Math.max(croppedScrollCoords.y1, croppedScrollCoords.y2) -
        Math.min(croppedScrollCoords.y1, croppedScrollCoords.y2);
      let pageFound = null;
      let docFound: HTMLElement | null = null;
      const Pages = invoice.DetectInvoiceResponse.OcrResult!.Pages;
      for (let i = 0; i < Pages.length; i++) {
        const doc = document.getElementById(`invoice-page-${Pages[i].Index}`);
        const docRect = doc!.getBoundingClientRect();
        if (
          croppedArealeft >= doc!.offsetLeft &&
          croppedArealeft <= doc!.offsetLeft + docRect.width &&
          croppedAreaTop >= doc!.offsetTop &&
          croppedAreaTop <= doc!.offsetTop + docRect.height
        ) {
          pageFound = Pages[i];
          docFound = doc;
          break;
        }
      }

      if (!pageFound) return;
      pageFound.Regions.forEach((region) => {
        region.Lines.forEach((line) => {
          line.Words.forEach((word) => {
            const { top, left, width, height } = ocrBoundingBoxToPosition(word.BoundingBox);
            const coordinates = {
              top: top * scale,
              left: left * scale,
              width: width * scale,
              height: height * scale,
            };

            if (
              croppedAreaTop <= coordinates.top + docFound!.offsetTop &&
              croppedArealeft <= coordinates.left + docFound!.offsetLeft &&
              croppedArealeft + croppedAreaWidth >=
                coordinates.left + coordinates.width + docFound!.offsetLeft &&
              croppedAreaHeight + croppedAreaTop >=
                coordinates.height + coordinates.top + docFound!.offsetTop
            ) {
              foundElements.push(word);
            }
          });
        });
      });

      let minusValue = 0;
      let countdown = pageFound.Index;
      while (countdown > 1){
        const clickedElement = document.getElementById(`invoice-page-${pageFound.Index - 1}`);
        minusValue = minusValue + clickedElement!.offsetHeight + 24;
        countdown--;
      }

      let xValues : any = [];
      let yValues : any = [];
      let widthValues : any = [];
      let heightValues : any = [];
      foundElements.forEach((item) => {
        xValues.push(parseInt(item.BoundingBox.Rectangle.split(',')[0]));
        yValues.push(parseInt(item.BoundingBox.Rectangle.split(',')[1]));
        widthValues.push(parseInt(item.BoundingBox.Rectangle.split(',')[2]));
        heightValues.push(parseInt(item.BoundingBox.Rectangle.split(',')[3]));
      });

      const minX = Math.min(...xValues) * scale;
      const minY = Math.min(...yValues) * scale;
      const maxHeight = Math.max(...heightValues) * scale;

      let croppedString = '';
      foundElements.forEach((w) => {
        croppedString = croppedString.concat(w.Text);
      });

      const width = Math.max(cropperCoordinates.x1, cropperCoordinates.x2) - Math.min(cropperCoordinates.x1, cropperCoordinates.x2);
      const height = Math.max(cropperCoordinates.y1, cropperCoordinates.y2) - Math.min(cropperCoordinates.y1, cropperCoordinates.y2);

      if (croppedString) {
        const calcWidth =  (parseInt(foundElements[foundElements.length - 1].BoundingBox.Rectangle.split(',')[0]) - parseInt(foundElements[0].BoundingBox.Rectangle.split(',')[0]) + parseInt(foundElements[foundElements.length - 1].BoundingBox.Rectangle.split(',')[2])) * scale;
        toast.info(`${formatMessage({ id: 'COPIED' })}: ${croppedString}`, { 
          theme: 'light' , 
          position: 'bottom-center',
          autoClose: 3000 
        });
        navigator.clipboard.writeText(croppedString);
        store.set('LassoValue', croppedString);
        store.set('LassoTop', minY);
        store.set('LassoLeft', minX);
        store.set('LassoHeight', maxHeight);
        store.set('LassoWidth', calcWidth);
        store.set('PageNumber', pageFound.Index - 1);
        store.set('NoValue', false);
      } else {
        if(!width && !height) return
        toast.info(formatMessage({ id: 'NOTCOPIED' }), { 
          theme: 'light' , 
          position: 'bottom-center',
          autoClose: 3000 
        });
        store.set('PageNumber', pageFound.Index - 1);
        store.set('NoValue', true);
        store.set('SetBBmMnual', true);
        store.set('NoValueLassoTop', croppedAreaTop - minusValue);
        store.set('NoValueLassoLeft', croppedArealeft);
        store.set('NoValueLassoHeight', height);
        store.set('NoValueLassoWidth', width);
      }
    }
  };
  const rem = getConfigFromStore();
  const removeGuiSplittingArea = rem ? rem.RemoveGuiSplittingArea : false;
  // const defaults = deriveFormValues(props);

  const getThresh = () => {
    const conf = getConfigFromStore();
    const invoiceDets = conf.InvoiceDetails;
    const thcpy = {};
    invoiceDets.forEach((el) => {
      if (el.Priority === 'MustHave') (thcpy as any)[el.Name] = el.AutoProcessableThreshold;
    });
    return thcpy;
  };
  const [detectionsThreshold, setDetectionsThreshold] = useState(getThresh());
  const onThresholdChange = (name: string, above: boolean) => {
    const thresholdCopy = { ...detectionsThreshold };
    (thresholdCopy as any)[name] = above;
    setDetectionsThreshold(thresholdCopy);
  };
  const order = useMemo(getInvoiceDetailTypesOrder, []);
  let clientAndSupplierCount = 0;
  order[1].forEach((item) => {
    if (item.type === 'Supplier' && !rem.IsSupplierVisible) clientAndSupplierCount++;
    if (item.type === 'Client' && !rem.IsClientVisible) clientAndSupplierCount++;
  });
  const width =
    removeGuiSplittingArea && order[1].length - clientAndSupplierCount > 0
      ? '367px 1fr'
      : '275px 1fr';
  const temp = () => {
    return `
       'detections pages .' 1fr
       / ${width}`;
  };
      
  lassoActive ? store.set('lassoActive', true) :  store.set('lassoActive', false)

  const setScoresToOne = (obj: any)=> {
    obj.Invoice.DetectInvoiceResponse.InvoiceDetailTypePredictions.forEach((item: any) => item.Score = 1);
    return obj;
  }

  return (
    <ErrorCountProvider>
      <Form
        defaultValues={deriveFormValues(props)}
        onSubmit={(values) => onSaveInvoice(setScoresToOne(values))}
        resetOnSuccessfulSubmit
      >
        {({ submit }) => (
          <>
          <Grid
            fill
            container
            template={({ spacing }) =>
              fullscreen
                ? `
          '. actionBar .' ${spacing(6)}
          '. pages .' 
         1fr/1fr 100fr`
                : temp()
            }
          >
            {lassoActive && (
              <LassoBox
                show={displayLassoArea}
                x1={cropperCoordinates.x1}
                x2={cropperCoordinates.x2}
                y1={cropperCoordinates.y1}
                y2={cropperCoordinates.y2}
              />
            )}
            <Dialog open={deleteDialog}>
              <Box padding={2}>
                <Flex container direction="column" align="center" justify="center" spacing={1}>
                  <ResolveOnTrigger
                    promise={onDeleteInvoice}
                    params={{ ContainerId: containerId, InvoiceId: invoice.InvoiceId }}
                    onResolve={() => setDeleteDialog(false)}
                  >
                    {({ trigger, loading, error, clearError }) =>
                      loading ? (
                        <Flex align="center" autoWidth alignSelf="center" item>
                          <CircularProgress style={{ left: 0, color: '#00aae9' }} />
                        </Flex>
                      ) : (
                        <>
                          {error && (
                            <Flex item>
                              <ErrInline error={error} onClose={clearError} />
                            </Flex>
                          )}

                          <Flex item>
                            <Text>
                              <FormattedMessage id="INVOICE_DETAIL_DELETE_DIALOG_TITLE" />
                            </Text>
                          </Flex>

                          <Flex item container justify="flex-end" spacing={1}>
                            <Flex item>
                              <Button onClick={() => setDeleteDialog(false)}>
                                <FormattedMessage id="CANCEL" />
                              </Button>
                            </Flex>

                            <Flex item>
                              <Button color="error" onClick={trigger}>
                                <FormattedMessage id="DELETE" />
                              </Button>
                            </Flex>
                          </Flex>
                        </>
                      )
                    }
                  </ResolveOnTrigger>
                </Flex>
              </Box>
            </Dialog>

            <Grid id="invoicePreviewer" area="pages" overflowing className="pages">
              {lassoActive && (
                <div
                  id="cropperArea"
                  onMouseDown={invoiceOnMouseDown}
                  onMouseMove={invoiceOnMouseMove}
                  onMouseUp={invoiceOnMouseUp}
                  style={{
                    position: 'absolute',
                    zIndex: 9,
                    display: 'inline-block',
                    //width: '100%',
                    width: zoomWidth + 'px',
                    height: totalOffest,
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                  }}
                ></div>
              )}
              {pagesWithDetections.map(({ detections, ...page }, i) => (
                <InvoiceDetailPage
                  ocrPageInfo={invoice.DetectInvoiceResponse.OcrResult!.Pages[i]}
                  lazyLoad={page.PageNo > eagerLoadCount}
                  thresholds={detectionsThreshold}
                  zoomWidth={zoomWidth as any}
                  returnHeight={returnHeight}
                  id={`invoice-page-${page.PageNo}`}
                  key={page.PageNo.toString()}
                  containerId={containerId}
                  onScaleChange={onScaleChange}
                  invoiceId={invoice.InvoiceId}
                  invoicePage={page}
                  ocrTextPageDetections={detections}
                  outlined={outlined}
                  magnifier={magnifier}
                  highlightedDetection={highlightedDetection}
                  onDetectionClick={setHighlightedDetection}
                />
              ))}
            </Grid>

            {!fullscreen && (
              <Grid
                area="detections"
                style={{ overflowX: 'hidden' }}
                overflowing
                className="detections"
              >
                <FormSubmitErrorState>
                  {(submitError, { resetSubmitError }) =>
                    submitError ? (
                      <Err error={submitError} onRetry={resetSubmitError} />
                    ) : (
                      <FormSubmittingState>
                        {(submitting) => (
                          <InvoiceDetailDetections
                            badClientId={badClientId}
                            badSupplierId={badSupplierId}
                            onThreshold={onThresholdChange}
                            saving={submitting}
                            highlightedDetection={highlightedDetection}
                            onHighlightDetection={setHighlightedDetection}
                            containerId={containerId}
                            pagesWithDetections={pagesWithDetections}
                          />
                        )}
                      </FormSubmittingState>
                    )
                  }
                </FormSubmitErrorState>
              </Grid>
            )}
          </Grid>

            <Flex container fill>
              <FormSubmittingState>
                {(submitting) => (
                  <InvoiceDetailActionBar
                    showRemove={rem.CanDeleteInvoiceInValidation}
                    onZoomInTrigger={onZoomInClick}
                    onZoomOutTrigger={onZoomOutClick}
                    fullscreen={fullscreen}
                    onTriggerFullscreen={() => setFullscreen((currFullscreen) => !currFullscreen)}
                    outlined={outlined}
                    onTriggerOutlined={() => setOutlined((currOutlined) => !currOutlined)}
                    lassoMode={lassoActive}
                    onTriggerLassoMode={() => setLassoActive((currentActive) => !currentActive)}
                    magnifier={magnifier}
                    onTriggerMagnifier={() => setMagnifier((currMagnifier) => !currMagnifier)}
                    downloadUrl={invoiceDownloadUrl}
                    downloadFileName={`${invoice.InvoiceId}.pdf`}
                    prevContainerUrl={prevContainerUrl}
                    nextContainerUrl={nextContainerUrl}
                    closeContainerUrl={closeContainerUrl}
                    onDelete={() => setDeleteDialog(true)}
                    saving={submitting}
                    onSave={submit}
                    invoiceState={invoice.InvoiceState}
                  />
                )}
              </FormSubmittingState>
            </Flex>
          </>

        )}
      </Form>
    </ErrorCountProvider>
  );
};

const ComposedInvoiceDetail = decorate(InvoiceDetail);
export { ComposedInvoiceDetail as InvoiceDetail };


